import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Shield, Plus, Edit2, Trash2, Loader2, 
  Check, X, DollarSign, Crown, Users 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminTiers() {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTier, setEditingTier] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const defaultFormData = {
    name: '',
    display_name: '',
    description: '',
    price_monthly: 0,
    stripe_price_id: '',
    sort_order: 0,
    is_active: true,
    highlight: false,
    features: {
      quizzes: true,
      flashcards: true,
      games: false,
      case_studies_per_month: 3,
      products_access: false,
      reference_sheets_pdf: false
    }
  };

  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const res = await fetch(`${API}/api/admin/tiers`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setTiers(data.tiers || []);
    } catch (error) {
      toast.error('Failed to load tiers');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (tier) => {
    setEditingTier(tier.tier_id);
    setFormData({
      name: tier.name,
      display_name: tier.display_name,
      description: tier.description,
      price_monthly: tier.price_monthly,
      stripe_price_id: tier.stripe_price_id || '',
      sort_order: tier.sort_order,
      is_active: tier.is_active,
      highlight: tier.highlight,
      features: { ...defaultFormData.features, ...tier.features }
    });
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingTier(null);
    setFormData({
      ...defaultFormData,
      sort_order: tiers.length + 1
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.display_name) {
      toast.error('Name and display name are required');
      return;
    }

    setSaving(true);
    try {
      const url = editingTier 
        ? `${API}/api/admin/tiers/${editingTier}`
        : `${API}/api/admin/tiers`;
      
      const res = await fetch(url, {
        method: editingTier ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to save');
      }

      toast.success(editingTier ? 'Tier updated' : 'Tier created');
      setShowForm(false);
      fetchTiers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tierId) => {
    if (!window.confirm('Are you sure you want to delete this tier?')) return;

    try {
      const res = await fetch(`${API}/api/admin/tiers/${tierId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to delete');
      }

      toast.success('Tier deleted');
      fetchTiers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Button
            data-testid="admin-tiers-back"
            onClick={() => navigate('/admin')}
            variant="ghost"
            className="hover:bg-slate-100 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6 text-amber-500" />
              <h1 className="text-2xl font-bold text-slate-900">Membership Tiers</h1>
            </div>
            <Button
              data-testid="add-tier-button"
              onClick={handleNew}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Tier
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Info Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
          <h3 className="font-semibold text-amber-800 mb-1">Stripe Price ID Required</h3>
          <p className="text-amber-700 text-sm">
            To enable subscriptions, create Products and Prices in your{' '}
            <a 
              href="https://dashboard.stripe.com/products" 
              target="_blank" 
              rel="noopener noreferrer"
              className="underline"
            >
              Stripe Dashboard
            </a>
            , then paste the Price ID (starts with <code className="bg-amber-100 px-1 rounded">price_</code>) here.
          </p>
        </div>

        {/* Tiers Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.tier_id}
              data-testid={`tier-item-${tier.name}`}
              className={`bg-white rounded-xl border-2 p-6 ${
                tier.highlight ? 'border-primary' : 'border-slate-200'
              } ${!tier.is_active ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-slate-900">{tier.display_name}</h3>
                    {tier.highlight && (
                      <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-full">Popular</span>
                    )}
                  </div>
                  {!tier.is_active && (
                    <span className="text-xs text-red-500">Inactive</span>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    data-testid={`edit-tier-${tier.name}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(tier)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`delete-tier-${tier.name}`}
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => handleDelete(tier.tier_id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="text-3xl font-bold text-slate-900 mb-2">
                ${tier.price_monthly.toFixed(2)}
                <span className="text-base font-normal text-slate-500">/mo</span>
              </div>

              <p className="text-sm text-slate-600 mb-4">{tier.description}</p>

              <div className="border-t border-slate-100 pt-4 space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  {tier.stripe_price_id ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <X className="w-4 h-4 text-red-400" />
                  )}
                  <span className={tier.stripe_price_id ? 'text-slate-700' : 'text-red-500'}>
                    {tier.stripe_price_id ? 'Stripe configured' : 'No Stripe Price ID'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {tier.features.games ? <Check className="w-4 h-4 text-green-500" /> : <X className="w-4 h-4 text-slate-300" />}
                  <span>Games</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>
                    {tier.features.case_studies_per_month === 0 
                      ? 'Unlimited Case Studies' 
                      : `${tier.features.case_studies_per_month} Case Studies/mo`}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Edit/Create Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6">
                {editingTier ? 'Edit Tier' : 'Create New Tier'}
              </h2>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Internal Name *
                    </label>
                    <Input
                      data-testid="tier-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                      placeholder="basic"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Display Name *
                    </label>
                    <Input
                      data-testid="tier-display-name-input"
                      value={formData.display_name}
                      onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                      placeholder="Basic Plan"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description
                  </label>
                  <textarea
                    data-testid="tier-description-input"
                    className="w-full border border-slate-200 rounded-lg p-3 text-sm"
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what's included..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Price ($/month)
                    </label>
                    <Input
                      data-testid="tier-price-input"
                      type="number"
                      step="0.01"
                      value={formData.price_monthly}
                      onChange={(e) => setFormData({ ...formData, price_monthly: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Sort Order
                    </label>
                    <Input
                      data-testid="tier-sort-input"
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Stripe Price ID
                  </label>
                  <Input
                    data-testid="tier-stripe-price-input"
                    value={formData.stripe_price_id}
                    onChange={(e) => setFormData({ ...formData, stripe_price_id: e.target.value })}
                    placeholder="price_1234567890abcdef"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Get this from your Stripe Dashboard &gt; Products &gt; Pricing
                  </p>
                </div>

                {/* Features */}
                <div className="border-t border-slate-100 pt-4">
                  <h3 className="font-medium text-slate-900 mb-3">Feature Access</h3>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={formData.features.games}
                        onChange={(e) => setFormData({
                          ...formData,
                          features: { ...formData.features, games: e.target.checked }
                        })}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      <span className="text-sm text-slate-700">Games (Matching, Word Search)</span>
                    </label>

                    <div>
                      <label className="block text-sm text-slate-700 mb-1">
                        Case Studies per Month (0 = unlimited)
                      </label>
                      <Input
                        data-testid="tier-case-limit-input"
                        type="number"
                        value={formData.features.case_studies_per_month}
                        onChange={(e) => setFormData({
                          ...formData,
                          features: { 
                            ...formData.features, 
                            case_studies_per_month: parseInt(e.target.value) || 0 
                          }
                        })}
                        className="w-32"
                      />
                    </div>

                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={formData.features.reference_sheets_pdf}
                        onChange={(e) => setFormData({
                          ...formData,
                          features: { ...formData.features, reference_sheets_pdf: e.target.checked }
                        })}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      <span className="text-sm text-slate-700">PDF Downloads</span>
                    </label>

                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={formData.features.products_access}
                        onChange={(e) => setFormData({
                          ...formData,
                          features: { ...formData.features, products_access: e.target.checked }
                        })}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      <span className="text-sm text-slate-700">Products Access</span>
                    </label>
                  </div>
                </div>

                {/* Options */}
                <div className="border-t border-slate-100 pt-4 flex gap-6">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-700">Active</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.highlight}
                      onChange={(e) => setFormData({ ...formData, highlight: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-700">Highlight as &quot;Popular&quot;</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  data-testid="cancel-tier-form"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="save-tier-button"
                  className="flex-1"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Tier'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
